// Thin fetch wrapper — no TanStack Query (see docs/architecture.md). Every
// call throws ApiError on a non-2xx response so callers can render a single
// consistent error path.

declare global {
  interface Window {
    __RAYMAND_CONFIG__?: { apiBaseUrl?: string };
  }
}

const BASE = (typeof window !== "undefined" && window.__RAYMAND_CONFIG__?.apiBaseUrl) || "";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let tokenGetter: () => string | null = () => null;
export function setTokenGetter(fn: () => string | null) {
  tokenGetter = fn;
}

async function request<T>(method: string, path: string, body?: any, opts?: { form?: FormData }): Promise<T> {
  const headers: Record<string, string> = {};
  const token = tokenGetter();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let payload: BodyInit | undefined;
  if (opts?.form) {
    payload = opts.form;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, { method, headers, body: payload });
  const text = await res.text();
  let data: any = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const message = (data && data.error) || `درخواست ناموفق بود (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return data as T;
}

export const api = {
  requestOtp: (zoodexMerchantId: string, mobile: string) =>
    request<{ sent: boolean; devCode?: string }>("POST", "/api/v1/auth/request-otp", { zoodexMerchantId, mobile }),
  verifyOtp: (zoodexMerchantId: string, mobile: string, code: string) =>
    request<{ token: string; merchantId: string; hasZoodexData: boolean; caseId: string; caseNumber: string }>(
      "POST", "/api/v1/auth/verify-otp", { zoodexMerchantId, mobile, code }
    ),
  staffLogin: (email: string, password: string) =>
    request<{ token: string; user: { id: string; fullName: string; email: string; role: string } }>(
      "POST", "/api/v1/auth/staff-login", { email, password }
    ),

  getCurrentCase: () => request<import("./types").CurrentCaseResponse>("GET", "/api/v1/cases/current"),
  setDataSource: (source: "ZOODEX" | "MANUAL") =>
    request<{ dataSource: string }>("POST", "/api/v1/cases/current/data-source", { source }),
  zoodexPrefill: () => request<{ applied: boolean; message?: string; fields?: Record<string, string> }>(
    "POST", "/api/v1/cases/current/zoodex-prefill"
  ),
  updateProfile: (fields: Record<string, string>) =>
    request<import("./types").MerchantProfile>("PATCH", "/api/v1/cases/current/profile", fields),
  upsertAddress: (body: import("./types").AddressRecord) =>
    request<{ status: string }>("PUT", "/api/v1/cases/current/address", body),
  getAddress: (entityType: string, entityId?: string) =>
    request<{ address: import("./types").AddressRecord; found: boolean }>(
      "GET", `/api/v1/cases/current/address?entityType=${entityType}${entityId ? `&entityId=${entityId}` : ""}`
    ),
  upsertGuarantor: (seq: number, g: Partial<import("./types").Guarantor>) =>
    request<import("./types").Guarantor>("PUT", `/api/v1/cases/current/guarantors/${seq}`, g),
  listGuarantors: () => request<import("./types").Guarantor[]>("GET", "/api/v1/cases/current/guarantors"),
  listDocuments: () => request<import("./types").DocumentRecord[]>("GET", "/api/v1/cases/current/documents"),
  uploadDocument: (entityType: string, entityId: string | undefined, documentType: string, file: File) => {
    const form = new FormData();
    form.append("entityType", entityType);
    form.append("entityId", entityId || "");
    form.append("documentType", documentType);
    form.append("file", file);
    return request<import("./types").DocumentVersion>("POST", "/api/v1/cases/current/documents", undefined, { form });
  },
  completeness: () => request<import("./types").Completeness>("GET", "/api/v1/cases/current/completeness"),
  submit: (consents: string[]) =>
    request<{ case: import("./types").CaseRecord; completeness: import("./types").Completeness }>(
      "POST", "/api/v1/cases/current/submit", { consents }
    ),
  caseStatus: () =>
    request<{ case: import("./types").CaseRecord; history: import("./types").StatusHistoryEntry[] }>(
      "GET", "/api/v1/cases/current/status"
    ),
  downloadUrl: (versionId: string) => `${BASE}/api/v1/documents/${versionId}/download`,

  // --- admin ---
  dashboard: () => request<import("./types").DashboardStats>("GET", "/api/v1/admin/dashboard"),
  adminListCases: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return request<import("./types").AdminCaseListResponse>("GET", `/api/v1/admin/cases?${qs}`);
  },
  adminCaseDetail: (id: string) => request<import("./types").AdminCaseDetail>("GET", `/api/v1/admin/cases/${id}`),
  adminChangeStatus: (id: string, status: string, note?: string) =>
    request<{ status: string }>("PATCH", `/api/v1/admin/cases/${id}/status`, { status, note }),
  adminSetBank: (id: string, branch: string) =>
    request<{ branch: string }>("PATCH", `/api/v1/admin/cases/${id}/bank`, { branch }),
  adminReviewDocument: (id: string, action: "approve" | "request-correction" | "reject", reason?: string) =>
    request<{ documentId: string; action: string }>("POST", `/api/v1/admin/documents/${id}/${action}`, { reason }),
  adminBankExport: (caseId: string) =>
    request<{ jobId: string; status: string }>("POST", `/api/v1/admin/cases/${caseId}/bank-export`),
  adminBankExportJobStatus: (jobId: string) =>
    request<{ id: string; caseId: string; status: string; bankExportId?: string; errorMessage?: string }>(
      "GET", `/api/v1/admin/bank-export-jobs/${jobId}`
    ),
  adminBankExportDownloadUrl: (exportId: string) => `${BASE}/api/v1/admin/bank-exports/${exportId}/download`,
};
